import React, { useEffect, useState, useMemo, useRef } from "react";
import StepParallelismi from "./StepParallelismi";
import StepSquadra from "./StepSquadra";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { fetchJson } from "../api";

const GOLD = "#d4a550";
const GOLD_DIM = "rgba(212,165,80,0.15)";
const GOLD_BORDER = "rgba(212,165,80,0.35)";
const BG = "#0a0a0f";
const SURFACE = "#12121a";
const BORDER = "rgba(255,255,255,0.07)";
const TEXT = "#e8e6df";
const TEXT_DIM = "rgba(232,230,223,0.45)";

const PRESET_STANDARD = [
  { typeKey: "ingest",           name: "Ingest",                baseDurationDays: 1, defaultLines: 1 },
  { typeKey: "logger",           name: "Logger",                baseDurationDays: 2, defaultLines: 1 },
  { typeKey: "taglio",           name: "Taglio contenuti",      baseDurationDays: 3, defaultLines: 1 },
  { typeKey: "montaggio",        name: "Montaggio",             baseDurationDays: 4, defaultLines: 3 },
  { typeKey: "finalizzazione",   name: "Finalizzazione",        baseDurationDays: 2, defaultLines: 1 },
  { typeKey: "vis_autore",       name: "Visione Autore",        baseDurationDays: 1, defaultLines: 1, isMilestone: true },
  { typeKey: "mod_autore",       name: "Modifiche post-Autore", baseDurationDays: 2, defaultLines: 1, isMilestone: true },
  { typeKey: "vis_rete",         name: "Visione Rete",          baseDurationDays: 1, defaultLines: 1, isMilestone: true },
  { typeKey: "mod_rete",         name: "Modifiche post-Rete",   baseDurationDays: 2, defaultLines: 1, isMilestone: true },
  { typeKey: "audio",            name: "Audio",                 baseDurationDays: 2, defaultLines: 1 },
  { typeKey: "color",            name: "Color",                 baseDurationDays: 2, defaultLines: 1 },
  { typeKey: "master",           name: "Consegna Master",       baseDurationDays: 1, defaultLines: 1, isMilestone: true },
];

const PRESET_MINIMAL = [
  { typeKey: "ingest",    name: "Ingest",    baseDurationDays: 1, defaultLines: 1 },
  { typeKey: "montaggio", name: "Montaggio", baseDurationDays: 4, defaultLines: 2 },
  { typeKey: "audio",     name: "Audio",     baseDurationDays: 2, defaultLines: 1 },
  { typeKey: "color",     name: "Color",     baseDurationDays: 2, defaultLines: 1 },
  { typeKey: "master",    name: "Master",    baseDurationDays: 1, defaultLines: 1 },
];

function addDays(iso, n) {
  if (!iso || typeof iso !== 'string') return iso;
  const d = new Date(iso + "T00:00:00Z");
  if (isNaN(d.getTime())) return iso;
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function nextWorkingDay(iso, workingDays) {
  let d = new Date(iso + "T00:00:00Z");
  for (let i = 0; i < 14; i++) {
    d.setUTCDate(d.getUTCDate() + 1);
    const dow = d.getUTCDay();
    const wd = dow === 0 ? 7 : dow;
    if (workingDays.includes(wd)) return d.toISOString().slice(0, 10);
  }
  return addDays(iso, 1);
}

function easterISO(y) {
  const a=y%19,b=Math.floor(y/100),c=y%100,d=Math.floor(b/4),e=b%4,
    f=Math.floor((b+8)/25),g=Math.floor((b-f+1)/3),h=(19*a+b-d-g+15)%30,
    i=Math.floor(c/4),k=c%4,l=(32+2*e+2*i-h-k)%7,
    m=Math.floor((a+11*h+22*l)/451),
    mo=Math.floor((h+l-7*m+114)/31),
    da=((h+l-7*m+114)%31)+1;
  return `${y}-${String(mo).padStart(2,"0")}-${String(da).padStart(2,"0")}`;
}

function italianHolidays(years) {
  const result = [];
  for (const y of years) {
    const easterMon = addDays(easterISO(y), 1);
    result.push(`${y}-01-01`,`${y}-01-06`,`${y}-04-25`,`${y}-05-01`,
      `${y}-06-02`,`${y}-08-15`,`${y}-11-01`,`${y}-12-08`,
      `${y}-12-25`,`${y}-12-26`,easterMon);
  }
  return [...new Set(result)].sort();
}

function Label({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_DIM, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>{children}</div>;
}

function Btn({ children, onClick, disabled, variant = "default", small }) {
  const base = { border: "none", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer", fontSize: small ? 11 : 13, fontWeight: 600, padding: small ? "4px 10px" : "8px 16px", transition: "all 0.15s", opacity: disabled ? 0.45 : 1 };
  const styles = {
    gold:    { background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, color: GOLD },
    ghost:   { background: "transparent", border: `1px solid ${BORDER}`, color: TEXT_DIM },
    default: { background: "rgba(255,255,255,0.06)", border: `1px solid ${BORDER}`, color: TEXT },
  };
  return <button onClick={onClick} disabled={disabled} style={{ ...base, ...styles[variant] }}>{children}</button>;
}

function StepDot({ n, active, done }) {
  return (
    <div style={{ width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, background: done ? GOLD_DIM : active ? "rgba(212,165,80,0.2)" : "rgba(255,255,255,0.04)", border: `1px solid ${done || active ? GOLD_BORDER : BORDER}`, color: done || active ? GOLD : TEXT_DIM }}>
      {done ? "✓" : n}
    </div>
  );
}

function StepSetup({ data, onChange }) {
  const WD = [{n:1,l:"Lun"},{n:2,l:"Mar"},{n:3,l:"Mer"},{n:4,l:"Gio"},{n:5,l:"Ven"},{n:6,l:"Sab"},{n:7,l:"Dom"}];
  function toggleWD(n) {
    const s = new Set(data.workingDays);
    s.has(n) ? s.delete(n) : s.add(n);
    onChange({ workingDays: [1,2,3,4,5,6,7].filter(x => s.has(x)) });
  }
  const inputStyle = { background: "rgba(255,255,255,0.04)", border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 13, padding: "7px 10px", outline: "none", width: "100%", boxSizing: "border-box" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <Label>Nome piano</Label>
        <input value={data.name} onChange={e => onChange({ name: e.target.value })} placeholder='Es. "Cortesie 23 — v1"' style={inputStyle} />
      </div>

      <div>
        <Label>Giorni lavorativi</Label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {WD.map(({ n, l }) => (
            <button key={n} onClick={() => toggleWD(n)} style={{ padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${data.workingDays.includes(n) ? GOLD_BORDER : BORDER}`, background: data.workingDays.includes(n) ? GOLD_DIM : "transparent", color: data.workingDays.includes(n) ? GOLD : TEXT_DIM }}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={data.includeNationalHolidays} onChange={e => onChange({ includeNationalHolidays: e.target.checked })} style={{ accentColor: GOLD }} />
          <span style={{ fontSize: 13, color: TEXT }}>Includi festivi nazionali IT + Pasquetta</span>
        </label>
      </div>
    </div>
  );
}

function StepEpisodes({ data, onChange }) {
  const [txCadence, setTxCadence] = React.useState('daily'); // daily | weekly
  const [txStart, setTxStart] = React.useState(data.txStart || '');
  const [arrivalStart, setArrivalStart] = React.useState(data.episodes[0]?.arrivalDate || '');
  const [deliveryDate, setDeliveryDate] = React.useState(data.deliveryDate || '');
  const [deliveryEpisode, setDeliveryEpisode] = React.useState(data.deliveryEpisode || '');
  const [useDeliveryForCalc, setUseDeliveryForCalc] = React.useState(data.useDeliveryForCalc || false);

  function addDays(dateStr, days) {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  }

  function fillTxSeries() {
    if (!txStart) return;
    onChange({ txStart });
    const eps = [];
    let lastDate = txStart;
    for (let i = 0; i < data.episodes.length; i++) {
      if (i === 0) {
        eps.push({ ...data.episodes[i], txDate: txStart });
        lastDate = txStart;
      } else {
        const next = txCadence === 'weekly' ? addDays(lastDate, 7) : nextWorkingDay(lastDate, data.workingDays);
        eps.push({ ...data.episodes[i], txDate: next });
        lastDate = next;
      }
    }
    onChange({ episodes: eps });
  }

  function fillArrival() {
    if (!arrivalStart) return;
    const eps = [];
    let lastDate = arrivalStart;
    for (let i = 0; i < data.episodes.length; i++) {
      if (i === 0) {
        eps.push({ ...data.episodes[i], arrivalDate: arrivalStart });
        lastDate = arrivalStart;
      } else {
        const next = nextWorkingDay(lastDate, data.workingDays);
        eps.push({ ...data.episodes[i], arrivalDate: next });
        lastDate = next;
      }
    }
    onChange({ episodes: eps });
  }

  function setEp(i, patch) {
    onChange({ episodes: data.episodes.map((e, idx) => idx === i ? { ...e, ...patch } : e) });
  }
  function setDelivery(d, ep, flag) {
    const nd = d !== undefined ? d : deliveryDate;
    const nep = ep !== undefined ? ep : deliveryEpisode;
    const nf = flag !== undefined ? flag : useDeliveryForCalc;
    setDeliveryDate(nd); setDeliveryEpisode(nep); setUseDeliveryForCalc(nf);
    onChange({ deliveryDate: nd, deliveryEpisode: nep, useDeliveryForCalc: nf });
  }

  function setCount(n) {
    const count = Math.max(1, Math.min(200, parseInt(n) || 1));
    const eps = Array.from({ length: count }, (_, i) => ({ episodeNo: i+1, txDate: data.episodes[i]?.txDate || "", arrivalDate: data.episodes[i]?.arrivalDate || "", deliveryDate: data.episodes[i]?.deliveryDate || "" }));
    onChange({ episodeCount: count, episodes: eps });
  }

  const cellInput = (val, onChange2) => (
    <input type="date" value={val} onChange={e => onChange2(e.target.value)}
      style={{ background: "transparent", border: `1px solid ${val ? BORDER : "rgba(239,68,68,0.4)"}`, borderRadius: 4, color: TEXT, fontSize: 12, padding: "3px 6px" }} />
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Riga N° episodi */}
      <div>
        <Label>N° episodi</Label>
        <input type="number" value={data.episodeCount} onChange={e => setCount(e.target.value)} min={1} max={200}
          style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 13, padding: "7px 10px", width: 80 }} />
      </div>

      {/* Barra TX */}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", padding: "10px 12px", background: "rgba(255,255,255,0.02)", borderRadius: 8, border: `1px solid ${BORDER}` }}>
        <div style={{ flex: 1, minWidth: 140 }}>
          <Label>Prima TX</Label>
          <input type="date" value={txStart} onChange={e => setTxStart(e.target.value)}
            style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 13, padding: "7px 10px", width: "100%", boxSizing: "border-box" }} />
        </div>
        <div>
          <Label>Cadenza</Label>
          <select value={txCadence} onChange={e => setTxCadence(e.target.value)}
            style={{ background: "#1a1d23", border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 13, padding: "7px 10px", outline: "none", colorScheme: "dark" }}>
            <option value="daily">Giornaliera</option>
            <option value="weekly">Settimanale</option>
          </select>
        </div>
        <Btn onClick={fillTxSeries} small disabled={!txStart}>Compila TX →</Btn>
      </div>

      {/* Barra Arrivo girato */}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", padding: "10px 12px", background: "rgba(255,255,255,0.02)", borderRadius: 8, border: `1px solid ${BORDER}` }}>
        <div style={{ flex: 1, minWidth: 140 }}>
          <Label>Primo arrivo girato</Label>
          <input type="date" value={arrivalStart} onChange={e => setArrivalStart(e.target.value)}
            style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 13, padding: "7px 10px", width: "100%", boxSizing: "border-box" }} />
        </div>
        <Btn onClick={fillArrival} small disabled={!arrivalStart}>Compila arrivo →</Btn>
      </div>
      <div style={{ overflowY: "auto", maxHeight: 380, border: `1px solid ${BORDER}`, borderRadius: 6, scrollbarWidth: "thin", scrollbarColor: "rgba(212,165,80,0.4) transparent" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
          <thead style={{ position: "sticky", top: 0, background: SURFACE, zIndex: 1 }}>
            <tr>
              {["Ep","Arrivo girato","TX"].map(h => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: TEXT_DIM, fontWeight: 600, fontSize: 11, borderBottom: `1px solid ${BORDER}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.episodes.map((e, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${BORDER}` }}>
                <td style={{ padding: "5px 12px", color: TEXT_DIM, fontWeight: 600, width: 40 }}>{e.episodeNo}</td>
                <td style={{ padding: "5px 12px" }}>{cellInput(e.arrivalDate, v => setEp(i, { arrivalDate: v }))}</td>
                <td style={{ padding: "5px 12px" }}>{cellInput(e.txDate, v => setEp(i, { txDate: v }))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pannello consegna master */}
      <div style={{ marginTop: 16, padding: '14px 16px', border: `1px solid rgba(212,165,80,0.2)`, borderRadius: 8, background: 'rgba(212,165,80,0.03)' }}>
        <div style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 10, lineHeight: 1.6 }}>
          Se hai una data di consegna master obbligatoria, indicala qui. Il calcolo delle deadline terrà conto di questa scadenza.
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 4 }}>Consegna master entro</div>
            <input type="date" value={deliveryDate} onChange={e => setDelivery(e.target.value, undefined, undefined)}
              style={{ background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 4, color: TEXT, fontSize: 13, padding: '5px 8px', colorScheme: 'dark' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 4 }}>Episodio di riferimento</div>
            <select value={deliveryEpisode} onChange={e => setDelivery(undefined, e.target.value, undefined)}
              style={{ background: '#12121a', border: `1px solid ${BORDER}`, borderRadius: 4, color: TEXT, fontSize: 13, padding: '5px 8px' }}>
              <option value="">— Seleziona —</option>
              {data.episodes.map(e => (
                <option key={e.episodeNo} value={e.episodeNo}>EP.{e.episodeNo}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
            <input type="checkbox" id="useDelivery" checked={useDeliveryForCalc}
              onChange={e => setDelivery(undefined, undefined, e.target.checked)}
              style={{ accentColor: GOLD, width: 16, height: 16, cursor: 'pointer' }} />
            <label htmlFor="useDelivery" style={{ fontSize: 12, color: useDeliveryForCalc ? GOLD : TEXT_DIM, cursor: 'pointer', fontWeight: useDeliveryForCalc ? 600 : 400 }}>
              Usa per il calcolo della deadline
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}


function StepTurni({ data, onChange }) {
  const episodeMins = data.episodeMins || 50;
  const minsPerDay = data.minsPerDay || 4;
  const giorni = Math.ceil(episodeMins / minsPerDay);
  const linee = Math.ceil(giorni / 5); // suggerisce linee per finire in ~1 settimana

  function apply() {
    const lineeFinali = Math.max(1, linee);
    const giorniPerMontatore = Math.ceil(giorni / lineeFinali);
    const phases = data.phases.map(p => {
      const isMontaggio = p.typeKey === "montaggio" ||
        (p.name || "").toLowerCase().includes("montaggio");
      return isMontaggio
        ? { ...p, baseDurationDays: giorniPerMontatore, defaultLines: lineeFinali }
        : p;
    });
    onChange({ phases, episodeMins, minsPerDay, _advanceStep: true });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ background: "rgba(212,165,80,0.06)", border: `1px solid ${GOLD_BORDER}`, borderRadius: 8, padding: "14px 18px" }}>
        <div style={{ fontSize: 12, color: GOLD, fontWeight: 600, marginBottom: 4 }}>Come funziona</div>
        <div style={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.6 }}>
          Inserisci la durata dell'episodio e la media giornaliera per montatore. Il sistema calcola automaticamente i giorni e il numero di linee suggerite per il montaggio.
        </div>
      </div>

      <div style={{ display: "flex", gap: 24 }}>
        <div style={{ flex: 1 }}>
          <Label>Durata episodio (minuti)</Label>
          <input type="number" min={1} value={data.episodeMins || 50}
            onChange={e => onChange({ episodeMins: Math.max(1, parseInt(e.target.value) || 1) })}
            style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 13, padding: "7px 10px", width: "100%", boxSizing: "border-box" }} />
        </div>
        <div style={{ flex: 1 }}>
          <Label>Minuti medi / giorno per montatore</Label>
          <input type="number" min={1} value={data.minsPerDay || 4}
            onChange={e => onChange({ minsPerDay: Math.max(1, parseInt(e.target.value) || 1) })}
            style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, fontSize: 13, padding: "7px 10px", width: "100%", boxSizing: "border-box" }} />
        </div>
      </div>

      <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "16px 20px", display: "flex", gap: 32, alignItems: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: GOLD }}>{giorni}</div>
          <div style={{ fontSize: 11, color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2 }}>giorni per episodio</div>
        </div>
        <div style={{ width: 1, height: 40, background: BORDER }} />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: GOLD }}>{Math.max(1, linee)}</div>
          <div style={{ fontSize: 11, color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2 }}>linee suggerite</div>
        </div>
        <div style={{ width: 1, height: 40, background: BORDER }} />
        <div style={{ flex: 1, fontSize: 12, color: TEXT_DIM, lineHeight: 1.5 }}>
          Con {Math.max(1,linee)} montator{Math.max(1,linee)===1?"e":"i"} in parallelo, ogni episodio viene completato in circa {Math.ceil(giorni / Math.max(1,linee))} giorni lavorativi.
        </div>
        <Btn onClick={apply} variant="gold">Applica al piano →</Btn>
      </div>

      <div style={{ fontSize: 12, color: TEXT_DIM, fontStyle: "italic" }}>
        * La media standard è 4 min/giorno. Potrai personalizzare la velocità dei singoli montatori quando crei la squadra.
      </div>
    </div>
  );
}

function StepStopFerie({ data, onChange }) {
  const stops = data.stopPeriods || [];
  const addStop = () => onChange({ stopPeriods: [...stops, { label: '', from: '', to: '' }] });
  const removeStop = (i) => onChange({ stopPeriods: stops.filter((_, idx) => idx !== i) });
  const updateStop = (i, field, val) => {
    const updated = stops.map((s, idx) => idx === i ? { ...s, [field]: val } : s);
    onChange({ stopPeriods: updated });
  };
  return (
    <div>
      <p style={{ fontSize: 13, color: TEXT_DIM, marginBottom: 16 }}>
        Aggiungi periodi in cui la produzione si ferma (chiusura aziendale, ferie collettive, ecc.).
        Questi giorni verranno saltati automaticamente nel calcolo del piano.
        Puoi saltare questo step se non ci sono stop previsti.
      </p>
      {stops.map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '10px 14px' }}>
          <input
            placeholder="Descrizione (es. Chiusura estiva)"
            value={s.label}
            onChange={e => updateStop(i, 'label', e.target.value)}
            style={{ flex: 2, background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, padding: '6px 10px', fontSize: 13 }}
          />
          <input
            type="date"
            value={s.from}
            onChange={e => updateStop(i, 'from', e.target.value)}
            style={{ flex: 1, background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, padding: '6px 10px', fontSize: 13 }}
          />
          <span style={{ color: TEXT_DIM, fontSize: 12 }}>→</span>
          <input
            type="date"
            value={s.to}
            onChange={e => updateStop(i, 'to', e.target.value)}
            style={{ flex: 1, background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: 6, color: TEXT, padding: '6px 10px', fontSize: 13 }}
          />
          <button onClick={() => removeStop(i)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>✕</button>
        </div>
      ))}
      <Btn onClick={addStop} variant="ghost" style={{ marginTop: 8 }}>+ Aggiungi stop</Btn>
    </div>
  );
}

function StepPhases({ data, onChange }) {
  const [epDuration, setEpDuration] = React.useState('');
  const [minutiAlGiorno, setMinutiAlGiorno] = React.useState('');
  function setPhases(phases) { onChange({ phases }); }
  function updatePhase(i, patch) { setPhases(data.phases.map((p, idx) => idx === i ? { ...p, ...patch } : p)); }
  function removePhase(i) { setPhases(data.phases.filter((_, idx) => idx !== i)); }
  function move(i, dir) {
    const next = [...data.phases];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setPhases(next);
  }
  const cellInput = (val, onChange2, w) => (
    <input value={val} onChange={e => onChange2(e.target.value)}
      style={{ background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 4, color: TEXT, fontSize: 12, padding: "3px 6px", width: w || "auto" }} />
  );
  const [giorniLavorazione, setGiorniLavorazione] = React.useState('');
  const nMontatori = epDuration && minutiAlGiorno && giorniLavorazione &&
    Number(minutiAlGiorno) > 0 && Number(giorniLavorazione) > 0
    ? Math.ceil(Number(epDuration) / (Number(minutiAlGiorno) * Number(giorniLavorazione)))
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* CALCOLATORE MONTATORI */}
      <div style={{ padding: "12px 16px", border: `1px solid rgba(212,165,80,0.35)`, borderRadius: 8, background: "rgba(212,165,80,0.04)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: GOLD, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>🎬 Calcolo montatori necessari</div>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 4 }}>Durata puntata (min)</div>
            <input type="number" min="1" step="0.5" value={epDuration} onChange={e => setEpDuration(e.target.value)}
              placeholder="es. 52"
              style={{ background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 4, color: TEXT, fontSize: 13, padding: "5px 8px", width: 100 }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 4 }}>Minuti montati/giorno per montatore</div>
            <input type="number" min="0.1" step="0.5" value={minutiAlGiorno} onChange={e => setMinutiAlGiorno(e.target.value)}
              placeholder="es. 8.5"
              style={{ background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 4, color: TEXT, fontSize: 13, padding: "5px 8px", width: 130 }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 4 }}>Giorni lavorazione per puntata</div>
            <input type="number" min="1" step="1" value={giorniLavorazione} onChange={e => setGiorniLavorazione(e.target.value)}
              placeholder="es. 5"
              style={{ background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 4, color: TEXT, fontSize: 13, padding: "5px 8px", width: 100 }} />
          </div>
          {nMontatori && (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ padding: "6px 14px", background: "rgba(212,165,80,0.12)", border: `1px solid rgba(212,165,80,0.4)`, borderRadius: 8 }}>
                <span style={{ fontSize: 12, color: GOLD }}>Montatori necessari: </span>
                <span style={{ fontSize: 20, fontWeight: 700, color: GOLD }}>{nMontatori}</span>
              </div>
              <Btn small variant="gold" onClick={() => {
                setPhases(data.phases.map(p =>
                  (p.name || '').toLowerCase().includes('montag')
                    ? { ...p, defaultLines: nMontatori, baseDurationDays: Number(giorniLavorazione) }
                    : p
                ));
              }}>Applica al montaggio ↓</Btn>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Btn onClick={() => setPhases(PRESET_STANDARD.map((p,i) => ({...p, sortOrder:i})))} small>Preset Standard TV</Btn>
        <Btn onClick={() => setPhases(PRESET_MINIMAL.map((p,i) => ({...p, sortOrder:i})))} small>Preset Minimale</Btn>
        <Btn onClick={() => {
          // Inserisce la nuova fase in posizione 1 (subito dopo Ingest se presente, altrimenti in cima).
          // Più comoda da posizionare rispetto al fondo della lista.
          const insertAt = data.phases.length > 0 ? 1 : 0;
          const newPhase = { typeKey: `custom_${Date.now()}`, name: "Nuova fase", baseDurationDays: 1, defaultLines: 1, sortOrder: insertAt, depPhaseSort: insertAt > 0 ? 0 : null, depType: "end", depOffsetDays: 0 };
          const next = [...data.phases.slice(0, insertAt), newPhase, ...data.phases.slice(insertAt)];
          setPhases(next);
        }} small variant="gold">+ Fase</Btn>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Header */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 70px 70px 1fr 80px", gap: 8, padding: "6px 12px", borderRadius: 4 }}>
          {["Fase","Gg lav.","Linee","Dipendenza",""].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
          ))}
        </div>
        {/* Rows */}
        <div style={{ overflowY: "auto", maxHeight: 420, display: "flex", flexDirection: "column", gap: 2, scrollbarWidth: "thin", scrollbarColor: "rgba(212,165,80,0.3) transparent" }}>
          {data.phases.map((p, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 70px 70px 1fr 80px", gap: 8, padding: "8px 12px", background: "rgba(255,255,255,0.03)", borderRadius: 6, border: `1px solid ${BORDER}`, alignItems: "center" }}>
              {/* Nome fase */}
              <input value={p.name} onChange={e => updatePhase(i, { name: e.target.value })}
                style={{ background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 4, color: TEXT, fontSize: 12, padding: "4px 8px", width: "100%", boxSizing: "border-box" }} />
              {/* Giorni */}
              <input type="number" min={1} value={p.baseDurationDays}
                onChange={e => updatePhase(i, { baseDurationDays: Math.max(1, parseInt(e.target.value)||1) })}
                style={{ background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 4, color: TEXT, fontSize: 12, padding: "4px 6px", width: "100%", boxSizing: "border-box", textAlign: "center" }} />
              {/* Linee */}
              <input type="number" min={1} value={p.defaultLines}
                onChange={e => updatePhase(i, { defaultLines: Math.max(1, parseInt(e.target.value)||1) })}
                style={{ background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 4, color: TEXT, fontSize: 12, padding: "4px 6px", width: "100%", boxSizing: "border-box", textAlign: "center" }} />
              {/* Dipendenza */}
              {i === 0 ? (
                <div style={{ fontSize: 11, color: TEXT_DIM, fontStyle: "italic" }}>Prima fase</div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap" }}>
                  <select
                    value={p.depPhaseSort != null ? p.depPhaseSort : i - 1}
                    onChange={e => {
                      const v = Number(e.target.value);
                      const target = data.phases[v];
                      updatePhase(i, { depPhaseSort: v, depPhaseTypeKey: target?.typeKey || null });
                    }}
                    style={{ background: "#1a1a1f", border: `1px solid ${BORDER}`, borderRadius: 4, color: TEXT, fontSize: 11, padding: "3px 4px", flex: 1, minWidth: 0 }}
                  >
                    {data.phases.slice(0, i).map((prev, pi) => (
                      <option key={pi} value={pi} style={{ background: "#1a1a1f" }}>{prev.name}</option>
                    ))}
                  </select>
                  {/* Toggle JKL style + label dinamica */}
                  <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                    <div
                      onClick={() => updatePhase(i, (!p.depType || p.depType === "end") ? { depType: "offset", depOffsetDays: p.depOffsetDays || 1 } : { depType: "end" })}
                      style={{ position: "relative", width: 32, height: 17, borderRadius: 9, cursor: "pointer", flexShrink: 0,
                        background: (!p.depType || p.depType === "end") ? "rgba(212,165,80,0.8)" : "rgba(255,255,255,0.12)",
                        border: `1px solid ${(!p.depType || p.depType === "end") ? "rgba(212,165,80,0.9)" : BORDER}`,
                        transition: "background 0.2s" }}>
                      <div style={{ position: "absolute", top: 2, width: 11, height: 11, borderRadius: "50%", background: "#fff", transition: "left 0.2s",
                        left: (!p.depType || p.depType === "end") ? 17 : 2 }} />
                    </div>
                    {(!p.depType || p.depType === "end") ? (
                      <span style={{ fontSize: 11, color: GOLD, whiteSpace: "nowrap" }}>dopo la fine</span>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <span style={{ fontSize: 11, color: TEXT_DIM, whiteSpace: "nowrap" }}>Dopo</span>
                        <input type="number" min={1} max={99}
                          value={p.depOffsetDays || ""}
                          onChange={e => updatePhase(i, { depOffsetDays: Math.max(1, parseInt(e.target.value)||1) })}
                          style={{ background: "transparent", border: `1px solid rgba(212,165,80,0.5)`, borderRadius: 4, color: GOLD, fontSize: 11, padding: "3px 4px", width: 36, textAlign: "center" }}
                        />
                        <span style={{ fontSize: 11, color: TEXT_DIM, whiteSpace: "nowrap" }}>gg dall'inizio</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Azioni */}
              <div style={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <button onClick={() => move(i,-1)} disabled={i===0} style={{ background:"none",border:"none",color:i===0?"rgba(255,255,255,0.15)":TEXT_DIM,cursor:i===0?"default":"pointer",fontSize:13,padding:"2px 4px" }}>↑</button>
                <button onClick={() => move(i,1)} disabled={i===data.phases.length-1} style={{ background:"none",border:"none",color:i===data.phases.length-1?"rgba(255,255,255,0.15)":TEXT_DIM,cursor:i===data.phases.length-1?"default":"pointer",fontSize:13,padding:"2px 4px" }}>↓</button>
                <button onClick={() => removePhase(i)} style={{ background:"none",border:"none",color:"rgba(239,68,68,0.5)",cursor:"pointer",fontSize:15,padding:"2px 4px" }}>×</button>
              </div>
            </div>
          ))}
        </div>
        {/* Legenda toggle */}
        <div style={{ fontSize: 11, color: TEXT_DIM, marginTop: 6, paddingLeft: 4, lineHeight: 1.5 }}>
          <span style={{ color: GOLD }}>Toggle acceso</span> = la fase parte dopo la fine di quella selezionata &nbsp;·&nbsp;
          <span style={{ color: TEXT }}>Toggle spento</span> = la fase parte N giorni lavorativi dopo l'<em>inizio</em> di quella selezionata
        </div>
      </div>
    </div>
  );
}

export default function CreatePlanPage() {
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const productionId = Number(searchParams.get("productionId"));
  const planId = Number(searchParams.get("planId")) || null;
  const isEdit = !!planId;

  useEffect(() => {
    if (!planId) return;
    fetchJson(`/postplans/${planId}`).then(({ plan, episodes, phases, episodePhaseConfig = [], lineLabels = [] }) => {
      setForm(f => ({
        ...f,
        name: plan.name || f.name,
        cadence: plan.cadence || f.cadence,
        workingDays: plan.working_days || f.workingDays,
        stopPeriods: Array.isArray(plan.stop_periods) ? plan.stop_periods : [],
        includeNationalHolidays: plan.include_national_holidays !== false,
        includeNationalHolidays: plan.include_national_holidays !== false,
        deliveryDate: plan.delivery_date ? plan.delivery_date.slice(0,10) : '',
        deliveryEpisode: plan.delivery_episode || '',
        useDeliveryForCalc: plan.use_delivery_for_calc || false,
        episodeCount: episodes.length || plan.episode_count || f.episodeCount,
        txStart: plan.tx_start_date ? plan.tx_start_date.slice(0,10) : f.txStart,
        episodes: episodes.length > 0 ? episodes.map(e => ({
          episodeNo: e.episode_no,
          txDate: e.tx_date ? e.tx_date.slice(0,10) : "",
          arrivalDate: e.material_arrival_date ? e.material_arrival_date.slice(0,10) : "",
        })) : Array.from({ length: plan.episode_count || f.episodeCount }, (_, i) => ({
          episodeNo: i + 1,
          txDate: "",
          arrivalDate: "",
        })),
        phases: phases.map((p, idx) => ({
          name: p.name,
          typeKey: p.type_key || `custom_${idx}`,
          baseDurationDays: p.base_duration_days || 1,
          defaultLines: p.default_lines || 1,
          sortOrder: p.sort_order ?? idx,
          depPhaseSort: p.dep_phase_sort != null ? Number(p.dep_phase_sort) : null,
          depType: p.dep_type || 'end',
          depOffsetDays: Number(p.dep_offset_days) || 0,
        })),
        parallelismi: episodePhaseConfig.reduce((acc, c) => {
          const ph = phases.find(p => p.id === c.phase_id);
          if (!ph) return acc;
          const key = `${c.episode_no}_${ph.type_key || ph.name}`;
          acc[key] = c.parallel_lines;
          return acc;
        }, {}),
        squadra: lineLabels.reduce((acc, ll) => {
          const ph = phases.find(p => p.id === ll.phase_id);
          if (!ph) return acc;
          const phKey = ph.type_key || ph.name;
          const key = `${phKey}_${ll.line_no}`;
          if (!acc[key]) {
            acc[key] = {
              label: ll.label || "",
              freelancerId: ll.freelancer_id || null,
              turni: ll.speed_multiplier ? Number(ll.speed_multiplier) : (ph.base_duration_days || 1),
              startDate: ll.available_from ? String(ll.available_from).slice(0,10) : "",
              endDate: ll.available_to ? String(ll.available_to).slice(0,10) : "",
            };
          }
          return acc;
        }, {}),
      }));
    }).catch(e => setErr("Errore caricamento piano: " + e.message));
  }, [planId]);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    name: "", cadence: "daily_5_7", workingDays: [1,2,3,4,5], includeNationalHolidays: true,
    episodeCount: 5, txStart: "",
    episodes: Array.from({length:5}, (_,i) => ({ episodeNo:i+1, txDate:"", arrivalDate:"" })),
    phases: PRESET_STANDARD.map((p,i) => ({...p, sortOrder:i})),
    episodeMins: 50,
    minsPerDay: 4,
    parallelismi: {},
    squadra: {},
  });
  function patch(obj) { setForm(f => ({...f, ...obj})); }

  useEffect(() => {
    if (form._advanceStep) {
      setForm(f => ({ ...f, _advanceStep: false }));
      setStep(s => Math.min(s + 1, STEPS.length - 1));
    }
  }, [form._advanceStep]);

  const holidays = useMemo(() => {
    if (!form.includeNationalHolidays) return [];
    const years = new Set();
    form.episodes.forEach(e => { if (e.txDate) { const y = parseInt(e.txDate); if (Number.isFinite(y) && y > 2000) years.add(y); } });
    if (form.txStart) { const y = parseInt(form.txStart); if (Number.isFinite(y) && y > 2000) years.add(y); }
    if (!years.size) years.add(new Date().getFullYear());
    return italianHolidays([...years]);
  }, [form.includeNationalHolidays, form.episodes, form.txStart]);

  const STEPS = ["Setup", "Episodi", "Fasi", "Parallelismi", "Squadra"];

  function validate() {
    if (!form.name.trim()) return "Inserisci il nome del piano";
    if (!productionId) return "productionId mancante nell'URL";
    if (form.workingDays.length === 0) return "Seleziona almeno un giorno lavorativo";
    if (form.episodes.some(e => !e.txDate)) return "Compila le date TX per tutti gli episodi";
    if (form.episodes.some(e => !e.arrivalDate)) return "Compila le date di arrivo girato per tutti gli episodi";
    if (form.phases.length === 0) return "Aggiungi almeno una fase";
    return "";
  }

  async function submit() {
    console.log('[submit] planId:', planId, 'isEdit:', isEdit, 'searchParams planId:', searchParams.get('planId'));
    const v = validate();
    if (v) { setErr(v); return; }
    setBusy(true); setErr("");
    try {
      const payload = {
        productionId, name: form.name.trim(), cadence: form.cadence,
        workingDays: form.workingDays, holidays, stopPeriods: form.stopPeriods || [], includeNationalHolidays: form.includeNationalHolidays !== false,
        episodeCount: form.episodeCount,
        txStartDate: form.episodes[0]?.txDate || null,
        includeNationalHolidays: form.includeNationalHolidays !== false,
        deliveryDate: form.deliveryDate || null,
        deliveryEpisode: form.deliveryEpisode || null,
        useDeliveryForCalc: form.useDeliveryForCalc || false,
        episodes: form.episodes.map(e => ({ episodeNo: e.episodeNo, txDate: e.txDate, materialArrivalDate: e.arrivalDate, arrivalDate: e.arrivalDate, deliveryDate: e.deliveryDate || null })),
        phases: form.phases.map((p,i) => {
          // Se abbiamo memorizzato depPhaseTypeKey (selezione esplicita utente), usa quello
          // per ricalcolare depPhaseSort: garantisce consistenza anche dopo spostamenti.
          let depSort = p.depPhaseSort != null ? Number(p.depPhaseSort) : (i > 0 ? i - 1 : null);
          if (p.depPhaseTypeKey) {
            const idx = form.phases.findIndex(ph => ph.typeKey === p.depPhaseTypeKey);
            if (idx >= 0 && idx < i) depSort = idx;
          }
          return { name: p.name, typeKey: p.typeKey, baseDurationDays: p.baseDurationDays, defaultLines: p.defaultLines, blocking: true, sortOrder: i, depPhaseSort: depSort, depType: p.depType || 'end', depOffsetDays: p.depOffsetDays || 0 };
        }),
        parallelismi: form.parallelismi || {},
        squadra: form.squadra || {},
      };
      const result = isEdit
        ? await fetchJson(`/postplans/${planId}`, { method: "PUT", body: JSON.stringify(payload) })
        : await fetchJson("/postplans", { method: "POST", body: JSON.stringify(payload) });
      nav(`/piani/${isEdit ? planId : result.id}/timeline`);
    } catch (e) { console.error('[submit error]', e); setErr(e.message || "Errore durante la creazione del piano"); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ minHeight: "100vh", background: BG, color: TEXT, fontFamily: "'Kanit','Inter',sans-serif", display: "flex", flexDirection: "column" }}>
      <div style={{ borderBottom: `1px solid ${BORDER}`, padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", background: SURFACE }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", color: TEXT_DIM, cursor: "pointer", fontSize: 18, padding: 0 }}>←</button>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{isEdit ? "Modifica piano di post-produzione" : "Nuovo piano di post-produzione"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {STEPS.map((label, i) => (
            <React.Fragment key={i}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <StepDot n={i+1} active={step===i} done={step>i} />
                <span style={{ fontSize: 12, color: step===i ? GOLD : TEXT_DIM, fontWeight: step===i ? 600 : 400 }}>{label}</span>
              </div>
              {i < STEPS.length-1 && <div style={{ width: 24, height: 1, background: BORDER }} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, padding: "32px 24px", maxWidth: 1000, width: "100%", margin: "0 auto", boxSizing: "border-box" }}>
        <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: 24 }}>
          <h2 style={{ margin: "0 0 20px 0", fontSize: 16, fontWeight: 700, color: GOLD }}>{STEPS[step]}</h2>
          {step === 0 && <StepSetup data={form} onChange={patch} />}
          {step === 1 && <StepEpisodes data={form} onChange={patch} />}

          {step === 2 && <StepPhases data={form} onChange={patch} />}
          {step === 3 && <StepParallelismi data={form} onChange={patch} />}
          {step === 4 && <StepSquadra data={form} onChange={patch} productionId={productionId} />}
          {err && <div style={{ marginTop: 16, padding: "10px 14px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, color: "#f87171", fontSize: 13 }}>{err}</div>}

          <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${BORDER}`, paddingTop: 16 }}>
            <Btn onClick={() => setStep(s => s-1)} disabled={step===0} variant="ghost">← Indietro</Btn>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn onClick={() => nav(-1)} variant="ghost">Annulla</Btn>
              {step < STEPS.length-1
                ? <Btn onClick={() => { setErr(""); setStep(s => s+1); }} variant="gold">Avanti →</Btn>
                : <Btn onClick={submit} disabled={busy} variant="gold">{busy ? "Creazione…" : "Crea piano"}</Btn>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
